import { useEffect, useMemo, useState } from 'react';

import {
  VendorTaxonomyKey,
  BAR_SERVICES_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorWordPlural } from '@zola-helpers/client/dist/es/marketplace/vendorWords';
import {
  CtaType,
  trackCtaClicked,
} from '@zola-helpers/client/dist/es/tracking/ctaEvents/ctaEvents';
import { getSessionStorage } from '@zola-helpers/client/dist/es/util/storage';
import H from '@zola/zola-ui/src/typography/Headings';

import VendorIcon from '~/components/common/icons/VendorIcon';
import useBookedVendors from '~/hooks/useBookedVendors';
import { AccountVendorView } from '~/types/responseTypes';
import { TrackingProductLocation } from '~/util/trackingHelper';

import CrossSellGuestList from '../CrossSell/CrossSellGuestList';
import { getAlternateCard } from '../CrossSell/utils';

import styles from './lowSupplyCrossSell.module.less';

export interface LowSupplyCrossSellProps {
  taxonomyKey?: VendorTaxonomyKey;
}

const bookingContainsVendor = (accountVendor: AccountVendorView) => {
  if (accountVendor.vendorType !== 'VENUE') return false;
  if (accountVendor.booked || accountVendor.vendorName !== null) return true;
  return false;
};

const SESSION_TOKEN = 'marketplaceCStokenSRP';
const GUEST_LIST_HREF = '/wedding/onboard/wedding-guest-list';

const TRACKING_DEFAULTS = {
  business_unit: 'MARKETPLACE',
  location: TrackingProductLocation.SEARCH_RESULTS,
  cta_type: CtaType.LINK,
  group_id: 'Cross Sell',
  position: 2,
  section: 'MARKETING',
};

/**
 * @name LowSupplyCrossSell
 * @description renders one of three cross-sell cards
 * based on whether the couple has booked a venue
 * if so, a random card (1/2) will be shown
 * if not, the guest list cross sell is shown
 * @param {VendorTaxonomyKey} taxonomyKey vendor type
 * @returns JSX.Component
 */
export const LowSupplyCrossSell = ({ taxonomyKey }: LowSupplyCrossSellProps) => {
  const [venueIsBooked, setVenueIsBooked] = useState(false);

  /** use a token to store a random setting for the session, and then alternate
   * GuestList (always shown if venue is not booked), otherwise
   * 0 = Website
   * 1 = Paper
   */
  const crossSellToken = useMemo(() => getSessionStorage(SESSION_TOKEN), []);
  const cardNumber = useMemo(() => Math.floor((Math.random() * 100) % 2), []);

  /* memoize component and props to avoid re-firing ctaViewed events */
  const { component: AlternateCrossSellCard, trackingProps: alternativeCardTrackingProps } =
    useMemo(() => {
      return getAlternateCard(cardNumber, crossSellToken);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

  /* load booked vendors to see if venue is booked */
  const { bookedVendors, loaded } = useBookedVendors();
  useEffect(() => {
    if (bookedVendors && bookedVendors.find(bookingContainsVendor)) {
      setVenueIsBooked(true);
    }
  }, [bookedVendors]);

  let vendorCopy = getVendorWordPlural(taxonomyKey || '');
  if (taxonomyKey === BAR_SERVICES_TAXONOMY_KEY) {
    vendorCopy = 'bar and beverage providers';
  }
  const hed = `We're busy adding more great ${vendorCopy}.`;

  /* if venue is booked, show an alternate cross sell */
  const CrossSellCard = venueIsBooked ? CrossSellGuestList : AlternateCrossSellCard;
  const trackingProps = venueIsBooked
    ? {
        cta_id: 'Cross Sell Guest List',
        cta: GUEST_LIST_HREF,
      }
    : alternativeCardTrackingProps;

  return (
    <div className={styles.lowSupplyCrossSell} data-testid="low-supply-cross-sell">
      <div className={styles.info}>
        {taxonomyKey && (
          <VendorIcon vendorTaxonomyKey={taxonomyKey} size={64} className={styles.vendorIcon} />
        )}
        <H.Title2 presentation="h4">{hed}</H.Title2>
      </div>
      {loaded && (
        <CrossSellCard
          handleClick={() => {
            trackCtaClicked({ ...trackingProps, ...TRACKING_DEFAULTS });
          }}
        />
      )}
    </div>
  );
};
